import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID, Renderer2, RendererFactory2 } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, throttleTime } from 'rxjs/operators';

interface prettyConsole {
    /**
    * log打印
    * @param textOrTitle 标题或内容
    * @param content 内容
    */
    log: Function;
    /**
    * info打印
    * @param textOrTitle 标题或内容
    * @param content 内容
    */
    info: Function;
    /**
    * error打印
    * @param textOrTitle 标题或内容
    * @param content 内容
    */
    error: Function;
    /**
    * warning打印
    * @param textOrTitle 标题或内容
    * @param content 内容
    */
    warning: Function;
    /**
    * success打印
    * @param textOrTitle 标题或内容
    * @param content 内容
    */
    success: Function;
}

@Injectable()
export class NcCommonService {
    private renderer: Renderer2;

    constructor(@Inject(PLATFORM_ID) private platformId: Object, private rendererFactory: RendererFactory2) {
        this.isBrowser = isPlatformBrowser(this.platformId);
        this.renderer = this.rendererFactory.createRenderer(null, null);
    }

    // ---------------------数字类---------------------

    /**
     * 精确加法
     * @param value1 第一个加数
     * @param value2 第二个加数
     * @returns 两数之和
     */
    mathAdd(value1: number, value2: number): number {
        // 将浮点数转换为字符串
        let aStr = value1.toString();
        let bStr = value2.toString();

        // 找到小数点后的位置
        let aDecimals = (aStr.split('.')[1] || '').length;
        let bDecimals = (bStr.split('.')[1] || '').length;

        // 找到最大的小数位数
        let maxDecimals = Math.max(aDecimals, bDecimals);

        // 将浮点数转换为整数
        let aInt = Math.round(value1 * Math.pow(10, maxDecimals));
        let bInt = Math.round(value2 * Math.pow(10, maxDecimals));

        // 进行整数加法
        let resultInt = aInt + bInt;

        // 将结果转换回浮点数
        return resultInt / Math.pow(10, maxDecimals);
    }

    /**
     * 精确减法
     * @param value1 被减数
     * @param value2 减数
     * @returns 两数之差
     */
    mathSubtract(value1: number, value2: number): number {
        // 将浮点数转换为字符串
        let aStr = value1.toString();
        let bStr = value2.toString();

        // 找到小数点后的位置
        let aDecimals = (aStr.split('.')[1] || '').length;
        let bDecimals = (bStr.split('.')[1] || '').length;

        // 找到最大的小数位数
        let maxDecimals = Math.max(aDecimals, bDecimals);

        // 将浮点数转换为整数
        let aInt = Math.round(value1 * Math.pow(10, maxDecimals));
        let bInt = Math.round(value2 * Math.pow(10, maxDecimals));

        // 进行整数减法
        let resultInt = aInt - bInt;

        // 将结果转换回浮点数
        return resultInt / Math.pow(10, maxDecimals);
    }

    /**
     * 精确乘法
     * @param value1 第一个乘数
     * @param value2 第二个乘数
     * @returns 两数之积
     */
    mathMultiply(value1: number, value2: number): number {
        // 将浮点数转换为字符串
        let aStr = value1.toString();
        let bStr = value2.toString();

        // 找到小数点后的位置
        let aDecimals = (aStr.split('.')[1] || '').length;
        let bDecimals = (bStr.split('.')[1] || '').length;

        // 将浮点数转换为整数
        let aInt = parseInt(aStr.replace('.', ''));
        let bInt = parseInt(bStr.replace('.', ''));

        // 进行整数乘法
        let resultInt = aInt * bInt;

        // 计算最终的小数位数
        let totalDecimals = aDecimals + bDecimals;

        // 将结果转换回浮点数
        return resultInt / Math.pow(10, totalDecimals);
    }

    /**
     * 精确除法
     * @param value1 被除数
     * @param value2 除数
     * @returns 两数之商
     */
    mathDivide(value1: number, value2: number): number {
        // 将浮点数转换为字符串
        let aStr = value1.toString();
        let bStr = value2.toString();

        // 找到小数点后的位置
        let aDecimals = (aStr.split('.')[1] || '').length;
        let bDecimals = (bStr.split('.')[1] || '').length;

        // 找到最大的小数位数
        let maxDecimals = Math.max(aDecimals, bDecimals);

        // 将浮点数转换为整数
        let aInt = Math.round(value1 * Math.pow(10, maxDecimals));
        let bInt = Math.round(value2 * Math.pow(10, maxDecimals));

        // 进行整数除法并直接返回结果
        return aInt / bInt;
    }

    /**
     * 保留指定小数位数
     * @param num 需要处理的数值
     * @param len 要保留的小数位数
     * @returns 处理后的数值，保留指定小数位数
     */
    retainDecimal(num: number, len: number): number {
        const n = Math.pow(10, len);
        return Math.round(num * n) / n;
    }

    /**
     * 保留指定小数位数的百分比
     * @param num 需要处理的数值
     * @param len 要保留的小数位数
     * @returns 处理后的百分比值，保留指定小数位数
     */
    retainPercentDecimal(num: number, len: number): number {
        const n = Math.pow(10, len + 2);
        return this.mathMultiply(Math.round(num * n) / n, 100);
    }

    // ---------------------存储---------------------

    /**
     * 设置Cookie
     * @param key Cookie的键
     * @param value Cookie的值
     * @param ttl Cookie的生存时间（毫秒）
     */
    setCookie(key: string, value: string, ttl: number): void {
        const expires = new Date(Date.now() + ttl).toUTCString();
        document.cookie = `${key}=${encodeURIComponent(value)};expires=${expires};path=/`;
    }

    /**
     * 获取Cookie
     * @param key Cookie的键
     * @returns Cookie的值，如果不存在则返回null
     */
    getCookie(key: string): string | null {
        const match = document.cookie.match(new RegExp('(^| )' + key + '=([^;]+)'));
        return match ? decodeURIComponent(match[2]) : null;
    }

    /**
     * 删除指定的Cookie
     * @param key 要删除的Cookie的键
     */
    removeCookie(key: string): void {
        document.cookie = `${key}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    }

    /**
     * 清除所有Cookie
     */
    clearCookie(): void {
        document.cookie.split(";").forEach(c => {
            document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/");
        });
    }

    /**
     * 设置LocalStorage项
     * @param key LocalStorage的键
     * @param value LocalStorage的值
     * @param ttl 可选的生存时间（毫秒）
     */
    setLocalStorage(key: string, value: any, ttl?: number): void {
        const item = { value, expiry: ttl ? Date.now() + ttl : null };
        localStorage.setItem(key, JSON.stringify(item));
    }

    /**
     * 获取LocalStorage项
     * @param key LocalStorage的键
     * @returns LocalStorage的值，如果不存在或已过期则返回null
     */
    getLocalStorage(key: string): any {
        const itemStr = localStorage.getItem(key);
        if (!itemStr) return null;
        const item = JSON.parse(itemStr);
        if (item.expiry && Date.now() > item.expiry) {
            localStorage.removeItem(key);
            return null;
        }
        return item.value;
    }

    /**
     * 删除指定的LocalStorage项
     * @param key 要删除的LocalStorage项的键
     */
    removeLocalStorage(key: string): void {
        localStorage.removeItem(key);
    }

    /**
     * 清除所有LocalStorage项
     */
    clearLocalStorage(): void {
        localStorage.clear();
    }

    /**
     * 设置SessionStorage项
     * @param key SessionStorage的键
     * @param value SessionStorage的值
     */
    setSessionStorage(key: string, value: any): void {
        sessionStorage.setItem(key, JSON.stringify(value));
    }

    /**
     * 获取SessionStorage项
     * @param key SessionStorage的键
     * @returns SessionStorage的值，如果不存在则返回null
     */
    getSessionStorage(key: string): any {
        const item = sessionStorage.getItem(key);
        return item ? JSON.parse(item) : null;
    }

    /**
     * 删除指定的SessionStorage项
     * @param key 要删除的SessionStorage项的键
     */
    removeSessionStorage(key: string): void {
        sessionStorage.removeItem(key);
    }

    /**
     * 清除所有SessionStorage项
     */
    clearSessionStorage(): void {
        sessionStorage.clear();
    }

    // ---------------------日期类---------------------

    /**
     * 格式化时间
     * @param type 格式化类型yyyy-MM-dd HH:mm:ss，注意MM为月份、mm为分钟
     * @param time 可选。默认当前时间
     * @returns yyyy-MM-dd HH:mm:ss
     */
    formatDate(type: string, time?: any): string {
        if (!time) {
            time = new Date();
        } else {
            time = new Date(time);
        }
        if (!type) {
            return '';
        }
        const year = time.getFullYear();
        const month = (time.getMonth() + 1).toString().padStart(2, '0');
        const date = (time.getDate()).toString().padStart(2, '0');
        const hours = (time.getHours()).toString().padStart(2, '0');
        const minute = (time.getMinutes()).toString().padStart(2, '0');
        const second = (time.getSeconds()).toString().padStart(2, '0');

        return type.replace('yyyy', year).replace('MM', month).replace('dd', date).replace('HH', hours).replace('mm', minute).replace('ss', second);
    }

    /**
     * 获取两日期间的所有日期
     * @param startDate 开始日期
     * @param endDate 结束日期
     * @returns 日期数组
     */
    getRangeDate(startDate: Date, endDate: Date): Date[] {
        let datesArray = [];
        let currentDate = new Date(startDate);

        while (currentDate <= endDate) {
            datesArray.push(new Date(currentDate));
            currentDate.setDate(currentDate.getDate() + 1);
        }
        return datesArray;
    }

    /**
    * 获取两日期间的天数
    * @param startDate 开始日期
    * @param endDate 结束日期
    * @returns 天数
    */
    getDaysBetween(startDate: Date, endDate: Date): number {
        const oneDay = 24 * 60 * 60 * 1000;
        return Math.round(Math.abs((startDate.getTime() - endDate.getTime()) / oneDay));
    }

    // ---------------------数组、对象---------------------

    /**
     * 数组去重
     * @param array 要去重的数组
     * @param keyFn 可选的函数，用于从每个数组项中提取用于比较的键，例如：item => item.id
     * @returns T[]
     */
    uniqueArray<T>(array: T[], keyFn?: (item: T) => any): T[] {
        if (!keyFn) {
            return Array.from(new Set(array));
        }

        const seen = new Set();
        return array.filter(item => {
            const key = keyFn(item);
            if (seen.has(key)) {
                return false;
            }
            seen.add(key);
            return true;
        });
    }

    /**
     * 数组排序
     * @param array 要排序的数组
     * @param order 指定排序顺序，'asc' 为升序，'desc' 为降序
     * @param keyFn 可选。可以是对象的键名或一个函数，用于从数组项中提取排序值，例如：'age'或 item => item.name.length
     * @returns T[]
     */
    sortArray<T>(array: T[], order: 'asc' | 'desc', key?: keyof T | ((item: T) => any)): T[] {
        return [...array].sort((a, b) => {
            let valueA: any;
            let valueB: any;

            if (typeof key === 'function') {
                valueA = key(a);
                valueB = key(b);
            } else if (key) {
                valueA = a[key];
                valueB = b[key];
            } else {
                valueA = a;
                valueB = b;
            }

            if (valueA === valueB) return 0;

            if (typeof valueA === 'string' && typeof valueB === 'string') {
                return order === 'asc'
                    ? valueA.localeCompare(valueB)
                    : valueB.localeCompare(valueA);
            }

            return order === 'asc'
                ? (valueA < valueB ? -1 : 1)
                : (valueB < valueA ? -1 : 1);
        });
    }

    /**
     * 判断对象是否为空
     * @param obj
     * @returns boolean
     */
    isObjectEmpty(obj: object): boolean {
        return Object.keys(obj).length === 0;
    }

    // ---------------------经纬度---------------------

    private x_PI = 3.14159265358979324 * 3000.0 / 180.0;
    private PI = 3.1415926535897932384626;
    private a = 6378245.0;
    private ee = 0.00669342162296594323;

    /**
     * 百度坐标系 (BD-09) 与火星坐标系 (GCJ-02) 的转换
     * 即百度 转 谷歌、高德
     * @param lng 经度
     * @param lat 纬度
     * @returns [lng, lat]
     */
    bd09togcj02(lng: number, lat: number): number[] {
        var x_pi = 3.14159265358979324 * 3000.0 / 180.0;
        var x = lng - 0.0065;
        var y = lat - 0.006;
        var z = Math.sqrt(x * x + y * y) - 0.00002 * Math.sin(y * x_pi);
        var theta = Math.atan2(y, x) - 0.000003 * Math.cos(x * x_pi);
        var gg_lng = z * Math.cos(theta);
        var gg_lat = z * Math.sin(theta);
        return [gg_lng, gg_lat]
    }

    /**
     * 火星坐标系 (GCJ-02) 与百度坐标系 (BD-09) 的转换
     * 即谷歌、高德 转 百度
     * @param lng 经度
     * @param lat 纬度
     * @returns [lng, lat]
     */
    gcj02tobd09(lng: number, lat: number): number[] {
        var z = Math.sqrt(lng * lng + lat * lat) + 0.00002 * Math.sin(lat * this.x_PI);
        var theta = Math.atan2(lat, lng) + 0.000003 * Math.cos(lng * this.x_PI);
        var bd_lng = z * Math.cos(theta) + 0.0065;
        var bd_lat = z * Math.sin(theta) + 0.006;
        return [bd_lng, bd_lat]
    }

    /**
     * WGS84转火星坐标系 (GCJ-02)
     * 即WGS84 转 谷歌、高德
     * @param lng 经度
     * @param lat 纬度
     * @returns [lng, lat]
     */
    wgs84togcj02(lng: number, lat: number): number[] {
        if (this.out_of_china(lng, lat)) {
            return [lng, lat]
        }
        else {
            var dlat = this.transformlat(lng - 105.0, lat - 35.0);
            var dlng = this.transformlng(lng - 105.0, lat - 35.0);
            var radlat = lat / 180.0 * this.PI;
            var magic = Math.sin(radlat);
            magic = 1 - this.ee * magic * magic;
            var sqrtmagic = Math.sqrt(magic);
            dlat = (dlat * 180.0) / ((this.a * (1 - this.ee)) / (magic * sqrtmagic) * this.PI);
            dlng = (dlng * 180.0) / (this.a / sqrtmagic * Math.cos(radlat) * this.PI);
            var mglat = lat + dlat;
            var mglng = lng + dlng;
            return [mglng, mglat]
        }
    }

    /**
     * 火星坐标系 (GCJ-02)转WGS84
     * 即谷歌、高德 转 WGS84
     * @param lng 经度
     * @param lat 纬度
     * @returns [lng, lat]
     */
    gcj02towgs84(lng: number, lat: number): number[] {
        if (this.out_of_china(lng, lat)) {
            return [lng, lat]
        }
        else {
            var dlat = this.transformlat(lng - 105.0, lat - 35.0);
            var dlng = this.transformlng(lng - 105.0, lat - 35.0);
            var radlat = lat / 180.0 * this.PI;
            var magic = Math.sin(radlat);
            magic = 1 - this.ee * magic * magic;
            var sqrtmagic = Math.sqrt(magic);
            dlat = (dlat * 180.0) / ((this.a * (1 - this.ee)) / (magic * sqrtmagic) * this.PI);
            dlng = (dlng * 180.0) / (this.a / sqrtmagic * Math.cos(radlat) * this.PI);
            let mglat = lat + dlat;
            let mglng = lng + dlng;
            return [lng * 2 - mglng, lat * 2 - mglat]
        }
    }

    private transformlat(lng: number, lat: number) {
        var ret = -100.0 + 2.0 * lng + 3.0 * lat + 0.2 * lat * lat + 0.1 * lng * lat + 0.2 * Math.sqrt(Math.abs(lng));
        ret += (20.0 * Math.sin(6.0 * lng * this.PI) + 20.0 * Math.sin(2.0 * lng * this.PI)) * 2.0 / 3.0;
        ret += (20.0 * Math.sin(lat * this.PI) + 40.0 * Math.sin(lat / 3.0 * this.PI)) * 2.0 / 3.0;
        ret += (160.0 * Math.sin(lat / 12.0 * this.PI) + 320 * Math.sin(lat * this.PI / 30.0)) * 2.0 / 3.0;
        return ret
    }

    private transformlng(lng: number, lat: number) {
        var ret = 300.0 + lng + 2.0 * lat + 0.1 * lng * lng + 0.1 * lng * lat + 0.1 * Math.sqrt(Math.abs(lng));
        ret += (20.0 * Math.sin(6.0 * lng * this.PI) + 20.0 * Math.sin(2.0 * lng * this.PI)) * 2.0 / 3.0;
        ret += (20.0 * Math.sin(lng * this.PI) + 40.0 * Math.sin(lng / 3.0 * this.PI)) * 2.0 / 3.0;
        ret += (150.0 * Math.sin(lng / 12.0 * this.PI) + 300.0 * Math.sin(lng / 30.0 * this.PI)) * 2.0 / 3.0;
        return ret
    }

    /**
     * 判断是否在国内，不在国内则不做偏移
     */
    private out_of_china(lng: number, lat: number) {
        return (lng < 72.004 || lng > 137.8347) || ((lat < 0.8293 || lat > 55.8271) || false);
    }

    /**
    * 小数形式经纬度转度分秒形式(秒保留3位小数)
    * @param digital  109.86330555555556
    * @returns degrees '109°51′47.9″'
    */
    digitalToDegrees(digital: number): string {
        let digitalString = digital.toString();
        let index1 = digitalString.indexOf('.');
        let du = index1 < 0 ? Number(digitalString) : Number(digitalString.substring(0, index1));//109
        let fen = 0;
        let miao = 0;
        if (index1 > 0) {
            let strFen = Number("0" + digitalString.substring(index1));//0.86330555555556
            let strFenString = (strFen * 60).toString();//'51.7983333333336'
            let index2 = strFenString.indexOf('.');
            if (index2 > 0) {
                fen = Number(strFenString.substring(0, index2));//51
                let strMiao = Number("0" + strFenString.substring(index2));//0.7983333333336
                miao = Number((strMiao * 60).toFixed(3));//47.900000000016  47.9
            } else {
                fen = Number(strFenString);
            }
        }

        return `${du}°${fen}′${miao}″`
    }

    /**
     * 度分秒形式经纬度转小数形式
     * @param degrees  '109°51′47.900000000016″'
     * @returns digital 109.86330555555556
     */
    degreesToDigital(degrees: string): number {
        let temp1 = degrees.split('°');
        let temp2 = temp1[1].split('′');
        let du = temp1[0];
        let fen = temp2[0];
        let miao = temp2[1].substring(0, temp2[1].length - 1);

        return Number(du) + Number(fen) / 60 + Number(miao) / 3600;
    }

    // ---------------------节流、防抖---------------------

    private debounceTasks: Map<string, Subject<Function>> = new Map();
    private throttleTasks: Map<string, Subject<Function>> = new Map();
    /**
     * 防抖（Debounce）：适用于避免短时间内多次触发的场景，只有在事件停止触发后的一段时间才会执行。例如：搜索框输入，避免频繁的API请求。
     * @param key 用于标识Subject的键
     * @param delay 防抖延迟时间（毫秒）
     * @param fn 要防抖的函数
     */
    debounce(key: string, delay: number, fn: Function): void {
        let subject = this.debounceTasks.get(key);

        if (!subject) {
            subject = new Subject<Function>();
            subject.pipe(debounceTime(delay)).subscribe(fnToExecute => {
                fnToExecute();
            });
            this.debounceTasks.set(key, subject);
        }

        subject.next(fn);
    }

    /**
    * 节流（Throttle）：适用于限制事件在固定时间间隔内执行的场景，确保事件不会过于频繁地触发。例如：滚动页面时的事件处理，避免频繁的计算和渲染。
    * @param key 用于标识Subject的键
    * @param delay 节流延迟时间（毫秒）
    * @param fn 要节流的函数
    */
    throttle(key: string, delay: number, fn: Function): void {
        let subject = this.throttleTasks.get(key);

        if (!subject) {
            subject = new Subject<Function>();
            subject.pipe(throttleTime(delay)).subscribe(fnToExecute => {
                fnToExecute();
            });
            this.throttleTasks.set(key, subject);
        }

        subject.next(fn);
    }

    // ---------------------消息提示---------------------

    private fullLoadingElement: HTMLElement | null = null;
    private fullLoadingKeyframesStyle: HTMLElement | null = null;

    /**
     * 显示全屏加载中（全屏蒙版）
     *  @param content 可选。提示内容，默认：'加载中，请稍后...'
     */
    showFullLoading(content: string = '加载中，请稍后...') {
        if (this.fullLoadingElement) {
            return;
        }

        this.fullLoadingElement = this.renderer.createElement('div');
        this.renderer.addClass(this.fullLoadingElement, 'full-loading-container');
        this.renderer.setStyle(this.fullLoadingElement, 'position', 'fixed');
        this.renderer.setStyle(this.fullLoadingElement, 'top', '0');
        this.renderer.setStyle(this.fullLoadingElement, 'left', '0');
        this.renderer.setStyle(this.fullLoadingElement, 'width', '100%');
        this.renderer.setStyle(this.fullLoadingElement, 'height', '100%');
        this.renderer.setStyle(this.fullLoadingElement, 'background-color', 'rgba(0,0,0,0.5)');
        this.renderer.setStyle(this.fullLoadingElement, 'display', 'flex');
        this.renderer.setStyle(this.fullLoadingElement, 'flex-direction', 'column');
        this.renderer.setStyle(this.fullLoadingElement, 'justify-content', 'center');
        this.renderer.setStyle(this.fullLoadingElement, 'align-items', 'center');
        this.renderer.setStyle(this.fullLoadingElement, 'z-index', '9999');

        const spinner = this.renderer.createElement('div');
        this.renderer.addClass(spinner, 'full-loading-spinner');
        this.renderer.setStyle(spinner, 'border', '4px solid #f3f3f3');
        this.renderer.setStyle(spinner, 'border-top', '4px solid #3498db');
        this.renderer.setStyle(spinner, 'border-radius', '50%');
        this.renderer.setStyle(spinner, 'width', '50px');
        this.renderer.setStyle(spinner, 'height', '50px');
        this.renderer.setStyle(spinner, 'animation', 'fullSpin 1s linear infinite');

        const loadingText = this.renderer.createElement('div');
        this.renderer.addClass(loadingText, 'full-loading-text');
        this.renderer.setStyle(loadingText, 'color', 'white');
        this.renderer.setStyle(loadingText, 'margin-top', '20px');
        this.renderer.setStyle(loadingText, 'font-size', '18px');
        this.renderer.setStyle(loadingText, 'letter-spacing', '1.5px');
        this.renderer.appendChild(loadingText, this.renderer.createText(content));

        const keyframes = `
            @keyframes fullSpin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
            }
        `;
        this.fullLoadingKeyframesStyle = this.renderer.createElement('style');
        this.renderer.appendChild(this.fullLoadingKeyframesStyle, this.renderer.createText(keyframes));
        this.renderer.appendChild(document.body, this.fullLoadingKeyframesStyle);

        this.renderer.appendChild(this.fullLoadingElement, spinner);
        if (content) {
            this.renderer.appendChild(this.fullLoadingElement, loadingText);
        }
        this.renderer.appendChild(document.body, this.fullLoadingElement);
    }

    /**
    * 移除全屏加载中（全屏蒙版）
    */
    removeFullLoading() {
        if (this.fullLoadingElement) {
            this.renderer.removeChild(document.body, this.fullLoadingElement);
            this.fullLoadingElement = null;
        }

        if (this.fullLoadingKeyframesStyle) {
            this.renderer.removeChild(document.body, this.fullLoadingKeyframesStyle);
            this.fullLoadingKeyframesStyle = null;
        }
    }

    // ---------------------调试---------------------

    /**
    * 美化控制台打印（log info error warning success）
    */
    console: prettyConsole = {
        log: (textOrTitle: any, content: any) => {
            const title = !content ? 'Log' : textOrTitle;
            const text = !content ? textOrTitle : content;
            this.prettyPrint(title, text, '#909399');
        },
        info: (textOrTitle: any, content: any) => {
            const title = !content ? 'Info' : textOrTitle;
            const text = !content ? textOrTitle : content;
            this.prettyPrint(title, text, '#40a9ff');
        },
        error: (textOrTitle: any, content: any) => {
            const title = !content ? 'Error' : textOrTitle;
            const text = !content ? textOrTitle : content;
            this.prettyPrint(title, text, '#F56C6C');
        },
        warning: (textOrTitle: any, content: any) => {
            const title = !content ? 'Warning' : textOrTitle;
            const text = !content ? textOrTitle : content;
            this.prettyPrint(title, text, '#E6A23C');
        },
        success: (textOrTitle: any, content: any) => {
            const title = !content ? 'Success' : textOrTitle;
            const text = !content ? textOrTitle : content;
            this.prettyPrint(title, text, '#67C23A');
        }
    }

    //美化打印
    private prettyPrint(title: string, content: any, color: string) {
        console.log(
            `%c ${title} %c ${typeof content == 'object' ? '👇' : content} %c`,
            `background:${color};border:1px solid ${color}; padding: 1px; border-radius: 2px 0 0 2px; color: #fff;`,
            `border:1px solid ${color}; border-radius: 0 2px 2px 0; color: ${color};padding: 1px;`,
            'background:transparent'
        );
        if (typeof content == 'object') {
            console.table(content);
        }
    };

    // 性能计时器
    private timers: { [key: string]: number } = {};

    /**
     * 开始性能计时器
     * @param timerName 计时器名称
     */
    startTimer(timerName: string): void {
        this.timers[timerName] = performance.now();
    }

    /**
     * 结束性能计时器并返回持续时间
     * @param timerName 计时器名称
     * @returns 计时器持续时间（毫秒）
     */
    endTimer(timerName: string): number {
        if (!this.timers[timerName]) {
            console.warn(`计时器 "${timerName}" 不存在。`);
            return 0;
        }
        const duration = performance.now() - this.timers[timerName];
        delete this.timers[timerName];
        return duration;
    }

    // ---------------------其他---------------------

    /**
    * 深克隆
    * @param obj 被克隆对象
    * @returns 克隆对象
    */
    deepClone<T>(obj: T, seen = new Map()): T {
        // Handle immutable types and functions
        if (obj === null || typeof obj !== 'object') {
            return obj;
        }

        // Handle circular references
        if (seen.has(obj)) {
            return seen.get(obj);
        }

        // Handle Date
        if (obj instanceof Date) {
            return new Date(obj.getTime()) as any;
        }

        // Handle RegExp
        if (obj instanceof RegExp) {
            const regExpClone = new RegExp(obj.source, obj.flags);
            regExpClone.lastIndex = obj.lastIndex; // Preserve the lastIndex property
            return regExpClone as any;
        }

        // Handle Array
        if (Array.isArray(obj)) {
            const arrClone: any[] = [];
            seen.set(obj, arrClone);
            for (const item of obj) {
                arrClone.push(this.deepClone(item, seen));
            }
            return arrClone as any;
        }

        // Handle Set
        if (obj instanceof Set) {
            const setClone = new Set();
            seen.set(obj, setClone);
            for (const item of obj) {
                setClone.add(this.deepClone(item, seen));
            }
            return setClone as any;
        }

        // Handle Map
        if (obj instanceof Map) {
            const mapClone = new Map();
            seen.set(obj, mapClone);
            for (const [key, value] of obj) {
                mapClone.set(this.deepClone(key, seen), this.deepClone(value, seen));
            }
            return mapClone as any;
        }

        // Handle Object
        const objClone = Object.create(Object.getPrototypeOf(obj));
        seen.set(obj, objClone);
        for (const key in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
                objClone[key] = this.deepClone((obj as any)[key], seen);
            }
        }
        return objClone;
    }

    /**
     * 获取文件后缀
     * @param filename string 文件名
     * @returns extension
     */
    getFileExtension(filename: string): string {
        if (!filename) {
            return '';
        }
        const filenameArr = filename.split('.');
        if (filenameArr.length == 0) {
            return '';
        } else {
            const extension = filenameArr.pop()!;
            return extension.toLowerCase();
        }
    }

    /**
     * jsonp封装
     * @param obj \{url: '', success: ()=> {}}
     */
    jsonpFunction(obj: any) {
        // 创建一个script标签;
        var script = document.createElement("script");
        // 给函数起名字，定义为全局函数；
        var fnName: any = "jsonp" + new Date().getTime();
        // window.aaa就是把aaa设置为全局变量！
        window[fnName] = obj.success;
        // 改变src,添加到head中
        if (obj.url.includes('?')) {
            script.src = obj.url + "&callback=" + fnName;
        } else {
            script.src = obj.url + "?callback=" + fnName;
        }
        // 把script标签添加到head标签中，就会发送src的请求了
        document.head.appendChild(script);
        // 代码执行完毕，把script标签删除
        script.onload = function () {
            document.head.removeChild(script);
            (window[fnName] as any) = undefined;
        }
    }

    /**
     * 是否客户端
     */
    isBrowser: boolean;

    /**
     * 生成随机颜色
     * @returns HEX
     */
    generateRandomColor(): string {
        return '#' + Math.floor(Math.random() * 16777215).toString(16);
    }

    /**
    * 生成UUID
    * @returns UUID
    */
    generateUUID(): string {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }

    /**
     * 验证密码强度（必须包含大、小写字母、数字、特殊字符至少三类，长度8-16位）
     * @param password 待验证的密码
     * @returns 密码是否符合要求
     */
    passwordValidate(password: string): boolean {
        // 检查密码长度是否在8到16位之间
        if (password.length < 8 || password.length > 16) {
            return false;
        }
    
        // 定义正则表达式规则
        let rC = {
            lW: '[a-z]',  // 小写字母
            uW: '[A-Z]',  // 大写字母
            nW: '[0-9]',  // 数字
            sW: '[`~!@$%\^\&\(\)\-_\+=\{\}\[\]|\\:;\"\'<>,\.\?\/_]'  // 特殊字符
        };

        /**
         * 检查字符串是否匹配正则表达式
         * @param str 待检查的字符串
         * @param rStr 正则表达式字符串
         * @returns 是否匹配
         */
        function Reg(str: string, rStr: string): boolean {
            let reg = new RegExp(rStr);
            return reg.test(str);
        }
    
        // 检查密码是否包含各种字符类型
        let tR = {
            l: Reg(password, rC.lW),  // 是否包含小写字母
            u: Reg(password, rC.uW),  // 是否包含大写字母
            n: Reg(password, rC.nW),  // 是否包含数字
            s: Reg(password, rC.sW)   // 是否包含特殊字符
        };
        
        // 密码必须包含以下四种组合之一：
        // 1. 小写字母 + 大写字母 + 数字
        // 2. 小写字母 + 大写字母 + 特殊字符
        // 3. 特殊字符 + 大写字母 + 数字
        // 4. 特殊字符 + 小写字母 + 数字
        return (tR.l && tR.u && tR.n) || (tR.l && tR.u && tR.s) || (tR.s && tR.u && tR.n) || (tR.s && tR.l && tR.n);
    }
}

